import type { DialogCheckedState } from "../../../../../components/UIToolkit/Popups/Dialog";

/**
 * The deactivation dialog's checkbox identities, and the one place that turns its checkbox state back
 * into a decision.
 *
 * A module of its own, with no React or MUI in it, for two reasons. It is the *contract* between the
 * descriptors the dialog builds and the booleans the mutation sends, so it deserves to be readable in
 * one screen — and this repo has no `@testing-library/react`, so the only way to get the contract
 * under vitest at all is for it to be a plain function rather than something reachable only by
 * rendering a MUI dialog. (`DialogCheckedState` is a type-only import, so nothing is added to the
 * runtime module graph.)
 *
 * **Why it is worth guarding (DAT-885 review M12).** Renaming `UNPOST_CHECKBOX_ID` used to be
 * completely silent: `states.find((s) => s.id === "unpost")` simply stopped matching, `?? false` made
 * that look like a deliberate "no", the operator was told the integration went off, and every open
 * posting stayed live at the vendor. `tsc` and the whole vitest suite passed. Nothing in the app can
 * notice a boolean that is wrong rather than missing, so the check has to live here.
 */

/**
 * Stable checkbox identities. `SystemDialog` falls back to the LABEL when a descriptor carries no id
 * (`checkboxIdentity`, `Popups/Dialog.tsx:56`), and both labels interpolate a live count — so without
 * these constants a refetched impact would change a checkbox's identity mid-dialog and silently reset
 * the tick the operator had already made.
 *
 * Exported so the descriptors and this resolver read from the SAME constant. Two string literals in
 * two files are exactly how the rename above went unnoticed.
 */
export const UNPOST_CHECKBOX_ID = "unpost";
export const ACKNOWLEDGE_CHECKBOX_ID = "ack";

const KNOWN_CHECKBOX_IDS: readonly string[] = [UNPOST_CHECKBOX_ID, ACKNOWLEDGE_CHECKBOX_ID];

export type AgencyDeactivationChoices = {
	/** Retract the department's still-live agency postings as part of switching off. */
	unpost: boolean;
	/** The required acknowledgement that already-scheduled agency clinicians stay on the schedule. */
	acknowledged: boolean;
};

/**
 * What the operator actually chose, resolved from `SystemDialog`'s checkbox state by **id**.
 *
 * Never by label: both labels are translated, and the un-post label interpolates a count that changes
 * when the impact preflight refetches.
 *
 * Absent vs unrecognised are treated as genuinely different things, which is the whole point:
 * - a **missing** known id is legitimate — the un-post checkbox is only rendered when there is
 *   something to retract, so its absence really does mean "no un-post", and that reads as `false`;
 * - an **unrecognised** id is a broken contract, and it THROWS. It can only mean a descriptor's id
 *   drifted from this resolver's, and the failure mode of quietly continuing is the one this function
 *   exists to prevent: a `false` that is indistinguishable from a deliberate choice. Same posture,
 *   and the same reasoning, as `assertUniqueCheckboxIdentities` throwing at the descriptor boundary
 *   in `Popups/Dialog.tsx` — a programmer error surfaces loudly at the boundary or not at all.
 */
export const resolveDeactivationChoices = (states?: DialogCheckedState[]): AgencyDeactivationChoices => {
	const present = states ?? [];
	const unrecognised = present.find((state) => state.id === undefined || !KNOWN_CHECKBOX_IDS.includes(state.id));
	if (unrecognised !== undefined) {
		throw new Error(
			`Unrecognised agency-deactivation checkbox id "${unrecognised.id ?? "<undefined>"}" (label: "${unrecognised.label}"). ` +
				`Expected one of: ${KNOWN_CHECKBOX_IDS.join(", ")}. A descriptor's id has drifted from resolveDeactivationChoices.`,
		);
	}
	const isChecked = (id: string): boolean => present.find((state) => state.id === id)?.checked ?? false;
	return { unpost: isChecked(UNPOST_CHECKBOX_ID), acknowledged: isChecked(ACKNOWLEDGE_CHECKBOX_ID) };
};
